// frontend/src/pages/auth/LoginPage.tsx

// Importando bibliotecas e componentes necessários
import React, { useState, useEffect } from "react";
import { useNavigate, Link as RouterLink, useLocation } from "react-router-dom";
import {
  Avatar,
  Button,
  CssBaseline,
  FormControlLabel,
  Checkbox,
  Link,
  Paper,
  Box,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import { 
  LockOutlined as LockOutlinedIcon,
  LanguageOutlined as LanguageIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as yup from "yup";

import { authService } from "../../services/authService";
import { translations } from "./login/loginTranslations";
import { LanguageSelector } from "../../components/auth/LanguageSelector";
import { userOnboardingService } from "../../services/userOnboardingService";
import { useStableLogin } from "../../hooks/useStableLogin";

// Importando constantes de cores do tema centralizado
import { 
  VERDE_MENTA, 
  VERDE_MENTA_ESCURO, 
  VERDE_MENTA_CLARO,
  FUNDO_ESCURO,
  FUNDO_PAPEL,
  TEXTO_PRIMARIO,
  TEXTO_SECUNDARIO,
  withAlpha
} from "../../themes/themeConstants";

// Configuração para internacionalização
/**
 * Erro devolvido pelo backend no login.
 * Substitui os `any` que existiam no catch: o formato é conhecido e o
 * compilador passa a cobrar verificação de quem lê os campos.
 */
interface LoginError {
  errorCode?: string;
  data?: { email?: string; userId?: string };
  response?: { data?: { message?: string; error?: string } };
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useStableLogin(); // Nova API simplificada
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Obter as traduções para o idioma atual, usando o idioma salvo se disponível
  const [currentLang, setCurrentLang] = useState<string>(
    location.state?.lang || "pt"
  );
  const t = translations[currentLang] || translations.pt;
  
  // Verificar se o usuário já está autenticado e redirecionar
  useEffect(() => {
    // Verificar se usuário já está logado, mas primeiro verificar se já estamos inicializados
    if (!initialized) {
      const isAuth = authService.isAuthenticated();
      if (isAuth) {
        // Se já estiver autenticado, redirecionar para dashboard
        const from = location.state?.from || "/dashboard";
        navigate(from, { replace: true });
      } else {
        // Marcar como inicializado para mostrar o formulário
        setInitialized(true);
      }
    }
  }, [navigate, location.state, initialized]);

  // Schema de validação
  const getValidationSchema = (lang: string) => {
    const translations_local = translations[lang] || translations.pt;
    
    return yup.object({
      email: yup
        .string()
        .email(translations_local.emailError)
        .required(translations_local.emailRequired),
      password: yup
        .string()
        .min(8, translations_local.passwordMin)
        .required(translations_local.passwordRequired),
    });
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      remember: false,
    },
    validationSchema: getValidationSchema(currentLang),
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔐 Iniciando login...');
        
        // Usar o hook estável que cuida de tudo
        const response = await login(values.email, values.password);

        // Setup inicial apenas se necessário (simplificado)
        if (response.user) {
          try {
            const hasVerifiedSetup = localStorage.getItem('user_setup_verified');
            if (!hasVerifiedSetup) {
              const setupStatus = await userOnboardingService.checkUserSetup(response.user.id);
              if (!setupStatus.hasSetup) {
                await userOnboardingService.createUserDefaultSetup(response.user.id, response.user.name);
              }
              localStorage.setItem('user_setup_verified', 'true');
            }
          } catch (setupError) {
            console.warn('⚠️ Erro no setup (não crítico):', setupError);
          }
        }

        console.log('✅ Login bem-sucedido! Redirecionamento automático via ApplicationManager.');
        
        // Não é necessário redirecionar manualmente - o ApplicationRouter faz isso
      } catch (err: unknown) {
        console.error("Erro ao fazer login:", err);

        const erro = err as LoginError;

        // ✅ Tratamento específico para email não verificado
        if (erro.errorCode === 'EMAIL_NOT_VERIFIED') {
          const errorData = erro.data;
          
          console.log('📧 Email não verificado detectado:', errorData);
          
          // Redirecionar para página de verificação com dados do usuário
          navigate('/auth/verify-email', {
            replace: true,
            state: {
              email: errorData?.email,
              userId: errorData?.userId,
              message: 'Sua conta ainda não foi verificada. Verifique seu email e clique no link de confirmação.',
              fromLogin: true
            }
          });
          return;
        }
        
        const errorMessage =
          erro.response?.data?.message ||
          erro.response?.data?.error ||
          t.errorDefault;
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  // Quando o idioma muda, precisamos reiniciar o formulário
  useEffect(() => {
    // Redefine apenas os erros de formulário, mantendo os valores
    // Não inclua o formik nas dependências para evitar loop
    formik.resetForm({ values: formik.values });
  }, [currentLang]);

  // Renderizar um loader enquanto verifica a autenticação
  if (!initialized) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100vw",
          bgcolor: FUNDO_ESCURO,
        }}
      >
        <CircularProgress sx={{ color: VERDE_MENTA }} />
      </Box>
    );
  }

  // Render login form once initialized
  return (
    <Box
      sx={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: FUNDO_ESCURO,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <CssBaseline />
      
      {/* Botão Home e Seletor de idioma */}
      <Box sx={{ position: 'absolute', top: 20, left: 20 }}>
        <Tooltip title="Voltar para a página inicial">
          <IconButton
            onClick={() => navigate('/')}
            size="medium"
            sx={{ 
              color: VERDE_MENTA,
              background: withAlpha(VERDE_MENTA, 0.1),
              '&:hover': {
                background: withAlpha(VERDE_MENTA, 0.2),
              }
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
          </IconButton>
        </Tooltip>
      </Box>
      
      {/* Seletor de idioma */}
      <LanguageSelector 
        currentLang={currentLang} 
        onChangeLang={setCurrentLang} 
      />
      
      {/* Conteúdo central - Card de login com tema escuro */}
      <Box
        sx={{
          width: { xs: "90%", sm: "450px" },
          maxWidth: "450px",
          backgroundColor: FUNDO_PAPEL,
          borderRadius: 3,
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
          border: `1px solid ${withAlpha(VERDE_MENTA, 0.2)}`,
          p: 4,
        }}
      >
        {/* Título do login */}
        <Typography 
          component="h1" 
          variant="h4" 
          align="center" 
          sx={{ 
            color: VERDE_MENTA, 
            fontWeight: 600,
            letterSpacing: 1,
            mb: 4
          }}
        >
          {t.loginTitle}
        </Typography>

        {/* Alerta de erro */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mt: 2, 
              mb: 2, 
              width: "100%",
              backgroundColor: "rgba(244, 67, 54, 0.15)",
              color: "#e57373",
              border: "1px solid rgba(244, 67, 54, 0.3)",
              borderRadius: 2,
            }}
          >
            {error}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{ width: "100%" }}
        >
          {/* Campo de E-mail */}
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            placeholder={t.username}
            name="email"
            autoComplete="email"
            autoFocus
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#3A3A3A",
                borderRadius: 2,
                "& fieldset": {
                  borderColor: "#555555",
                },
                "&:hover fieldset": {
                  borderColor: "#777777",
                },
                "&.Mui-focused fieldset": {
                  borderColor: VERDE_MENTA,
                },
              },
              "& .MuiInputBase-input": {
                color: TEXTO_PRIMARIO,
              },
              "& .MuiFormHelperText-root": {
                color: "#F44336",
              },
            }}
            InputProps={{
              sx: { py: 1.5 }
            }}
          />

          {/* Campo de Senha */}
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            placeholder={t.password}
            type={showPassword ? "text" : "password"}
            id="password"
            autoComplete="current-password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#3A3A3A",
                borderRadius: 2,
                "& fieldset": {
                  borderColor: "#555555",
                },
                "&:hover fieldset": {
                  borderColor: "#777777",
                },
                "&.Mui-focused fieldset": {
                  borderColor: VERDE_MENTA,
                },
              },
              "& .MuiInputBase-input": {
                color: TEXTO_PRIMARIO,
              },
              "& .MuiFormHelperText-root": {
                color: "#F44336",
              },
            }}
            InputProps={{
              sx: { py: 1.5 },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: VERDE_MENTA }}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ 
              mt: 2, 
              mb: 3,
              py: 1.5,
              backgroundColor: VERDE_MENTA, // Botão verde menta
              color: "white",
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              "&:hover": {
                backgroundColor: VERDE_MENTA_ESCURO,
              }
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : t.signIn}
          </Button>

          {/* Link de recuperação de senha */}
          <Box 
            sx={{ 
              display: "flex", 
              justifyContent: "center", 
              alignItems: "center",
              gap: 1,
              mb: 2 
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                color: TEXTO_SECUNDARIO,
                fontWeight: 400,
              }}
            >
              {t.forgotPassword}
            </Typography>
            <Typography
              onClick={() => navigate('/esqueci-senha')}
              sx={{
                color: VERDE_MENTA,
                fontWeight: 500,
                textDecoration: 'none',
                cursor: 'pointer'
              }}
            >
              {t.clickHere}
            </Typography>
          </Box>
          
          {/* Link para registro */}
          <Box 
            sx={{ 
              display: "flex", 
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5 
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: TEXTO_SECUNDARIO,
                  fontWeight: 400,
                }}
              >
                {t.noAccount}
              </Typography>
              <Typography
                onClick={() => navigate('/register')}
                sx={{
                  color: VERDE_MENTA,
                  fontWeight: 500,
                  textDecoration: 'none',
                  cursor: 'pointer'
                }}
              >
                {t.signUp}
              </Typography>
            </Box>

            {/* Registro com LEX (assistente IA) desligado da tela de login em
                30/08/2026 a pedido do Alcélio: o fluxo está em revisão e não
                deve receber usuário novo enquanto isso. A rota /register-lex e
                a página seguem existindo para retomada. */}
          </Box>
        </Box>
      </Box>
      
      {/* Removido debugger de navegação */}
    </Box>
  );
};

export default LoginPage;